import { gql, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkflow, fetchWorkflowRequest } from 'modules/workflows/ducks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';

export const optimizeJourneyMutation = gql`
  mutation optimizeJourneyMutation(
    $workflowId: Long!
    $optimizationType: JourneyOptimizationType!
    $nodeIds: [[Long!]!]!
  ) {
    optimizeJourney(
      workflowId: $workflowId
      optimizationType: $optimizationType
      nodeIds: $nodeIds
    ) {
      id
      workflowType
    }
  }
`;

type useOptimizeJourneyProps = {
  nodeIds: number[][];
  optimizationType: string;
  onSuccess?: () => void;
};

export const TYPE_TO_TOAST_MESSAGE_MAP: Record<string, string> = {
  MergeStartYesNoSplitTiles: 'Yes/No Split tile and Start tile combined.',
  MergeChainedChannelSubscriptionTiles: 'Subscription preference tiles combined.',
  MergeChainedMessageSubscriptionTiles: 'Subscription preference tiles combined.',
};

const FAILURE_MESSAGE = `Error optimizing journey. Please try again later.`;

export const useOptimizeJourney = () => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const { id: workflowId } = workflow;

  const [optimizeJourney, { loading }] = useMutation(optimizeJourneyMutation);

  const optimizeReviewItem = async ({
    nodeIds,
    optimizationType,
    onSuccess,
  }: useOptimizeJourneyProps): Promise<any> => {
    try {
      const { data } = await optimizeJourney({
        variables: {
          workflowId,
          optimizationType,
          nodeIds,
        },
      });
      if (data) {
        if (onSuccess) onSuccess();
        dispatch(
          fetchWorkflowRequest(data?.optimizeJourney?.id, data?.optimizeJourney?.workflowType),
        );
        dispatch(addPositiveToast(TYPE_TO_TOAST_MESSAGE_MAP[optimizationType]));
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useOptimizeJourney'));
      }
    } catch (ex) {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useOptimizeJourney'));
    }
  };

  return {
    optimizeReviewItem,
    isOptimizing: loading,
  };
};
